import { forwardRef, ReactNode, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import type { ItemClickEvent, Item as MenuItem } from 'devextreme/ui/menu';
import { COLUMN_PARTNER_MINWIDTH, DX_DATETIME_DISPLAY_FORMAT, uaFilterRowText } from 'app-constants';
import { SearchDropdown } from 'components/search-dropdown/search-dropdown';
import { dsPartners, dsOrders } from 'datasources';
import { DataGrid, Menu } from 'devextreme-react';
import { Column, FilterRow, Lookup, Paging, Scrolling, Selection } from 'devextreme-react/data-grid';
import { useSearchDatagridHandler } from 'hooks';
import { CustomStoreExt } from 'types/datatypes/classes';

export interface OrderItem {
  ref: string;
  caption?: string;
}

export interface OrderFilterData {
  partnerRef?: string;
  dateLimit?: string;
}

interface OrdersMenuItem extends MenuItem {
  id?: string;
}

interface OrdersDatagridProps {
  searchField?: string;
  filterData?: OrderFilterData;
  dataSource?: CustomStoreExt | null;
  onSelectionChanged?: (row: OrderItem) => void;
  value?: OrderItem | null;
}

interface OrdersSearchProps {
  order?: OrderItem | null;
  filterData?: OrderFilterData;
  onSelect: (data: OrderItem) => void;
  onOpenDocButton?: () => void;
  onDetailButton?: () => void;
  detailComponent?: ReactNode;
  dataSource?: CustomStoreExt | null;
  readOnly?: boolean;
  stylingMode?: string;
  validator?: ReactNode;
  [key: string]: unknown;
}

const OrdersDatagrid = forwardRef<unknown, OrdersDatagridProps>(
  ({
    searchField = 'number_doc',
    filterData,
    dataSource: propsDataSource,
    onSelectionChanged: propsOnSelectionChanged,
    value,
  }, ref) => {
    const navigate = useNavigate();

    const [ currentRowData, setCurrentRowData ] = useState<OrderItem | null>(null);
    const [ byPartner, setByPartner ] = useState(!!filterData?.partnerRef);
    const [ dataSource ] = useState<CustomStoreExt>(propsDataSource ?? dsOrders);

    if (byPartner) {
      dataSource.filter([ 'partner', '=', filterData?.partnerRef ]);
    } else {
      dataSource.filter();
    }

    useEffect(() => {
      setByPartner(!!filterData?.partnerRef);
    }, [ filterData?.partnerRef ]);

    useEffect(
      () => () => {
        dataSource.userData = undefined;
        dataSource.filter();
      },
      [ dataSource ]
    );

    const _onSelectionChanged = (row: OrderItem) => {
      dataSource.byKey(row.ref).then(propsOnSelectionChanged);
    };

    const { gridRef, focusElementRef, dataReady, onCurrentRowChange } = useSearchDatagridHandler(
      ref,
      searchField,
      _onSelectionChanged
    );

    useEffect(() => {
      dataSource.userData = { globalSearch: !!byPartner };
      gridRef.current!.instance.refresh();
    }, [ byPartner, dataSource, gridRef ]);

    const clickMenu = (e: ItemClickEvent) => {
      const item = e.itemData as OrdersMenuItem;
      if (item?.id === 'select') {
        currentRowData && _onSelectionChanged(currentRowData);
      } else if (item?.id === 'open') {
        navigate(`/order/${value?.ref}`);
      } else if (item?.id === 'byPartner') {
        setByPartner(!byPartner);
      }
    };

    const menuItems: OrdersMenuItem[] = [
      {
        text: 'Вибрати',
        id: 'select',
        icon: 'check',
        visible: !!propsOnSelectionChanged,
        disabled: !currentRowData,
      },
      {
        text: 'Відкрити',
        id: 'open',
        icon: 'find',
        disabled: !value?.ref,
      },
      {
        text: byPartner ? 'По контрагенту' : 'Всі',
        id: 'byPartner',
        icon: 'preferences',
        selected: byPartner,
        visible: !!filterData?.partnerRef,
      },
    ];

    return (
      <div ref={focusElementRef} style={{ display: 'block', width: '100%', height: '100%' }}>
        <Menu
          onItemClick={clickMenu}
          activeStateEnabled={false}
          dataSource={menuItems}
        />
        <DataGrid
          ref={gridRef}
          showBorders
          remoteOperations
          dataSource={dataReady ? dataSource : []}
          hoverStateEnabled={true}
          onRowDblClick={(e) => _onSelectionChanged(e.data as OrderItem)}
          onSelectionChanged={(e) => {
            if (e.selectedRowsData.length > 0) {
              setCurrentRowData(e.selectedRowsData[0] as OrderItem);
              onCurrentRowChange(e.selectedRowsData[0] as OrderItem);
            }
          }}
          height='90%'
        >
          <Selection mode='single' />
          <Scrolling mode='virtual' rowRenderingMode='virtual' />
          <Paging enabled={true} pageSize={100} />
          <FilterRow visible={true} {...uaFilterRowText} />

          <Column dataField='number_doc' caption='Номер' alignment='center' width={150} />

          <Column
            dataField='date'
            caption='Дата'
            dataType='date'
            format={DX_DATETIME_DISPLAY_FORMAT}
            alignment='center'
            width={190}
          />

          <Column
            allowSorting={false}
            dataField='partner.ref'
            caption='Контрагент'
            dataType='string'
            alignment='left'
            allowResizing={false}
            calculateDisplayValue={(data: Record<string, unknown>) => (data.partner as { name: string })?.name}
            minWidth={COLUMN_PARTNER_MINWIDTH}
            allowFiltering={!byPartner}
          >
            <Lookup
              dataSource={dsPartners}
              allowClearing={true}
              valueExpr='ref'
              displayExpr='name'
              // minSearchLength={3}
              // searchTimeout={500}
            />
          </Column>
        </DataGrid>
      </div>
    );
  }
);

OrdersDatagrid.displayName = 'OrdersDataGrid';

export const OrdersSearch = (props: OrdersSearchProps) => (
  <SearchDropdown
    {...props}
    value={props.order || { ref: '', caption: '' }}
    text={props.order?.caption || ''}
    onSelect={props.onSelect}
    minSearchLength={3}
    searchTimeout={500}
    placeholder='Замовлення...'
    saveSearchValueOnLeave={true}
    width='100%'
    popupMode='popup'
    dropdownWidth={700}
    dropdownHeight={500}
    onOpenDocButton={props?.onOpenDocButton}
  >
    <OrdersDatagrid
      dataSource={props.dataSource}
      value={props.order}
      filterData={props.filterData}
    />
  </SearchDropdown>
);
